import { useState, useEffect } from "react";
import { FiCopy, FiShare2 } from "react-icons/fi";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ShareModal from "../components/games/auxiliary/ShareModal";
import CustomSvg from "../components/svgs/CustomSvg";
import { getRequest, patchRequest } from "../components/apiRequests/requestApi";

const INITIAL_MODAL_STATE = {
  visible: false,
  onHide: null,
  size: "md",
};

const TournamentCode = () => {
  const [code, setCode] = useState("ABC12345");
  const [shareModal, setShareModal] = useState(INITIAL_MODAL_STATE);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [codeLoading, setCodeLoading] = useState(false);
  const { gameId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        const json = await getRequest(`/celebrity/tournament/${gameId}`);

        if (!json?.data?.joiningCode) {
          setShowCodeModal(true);
        } else {
          setCode(json.data.joiningCode);
        }
      } catch (err) {
        toast.error("Failed to load tournament data");
      } finally {
        setLoading(false);
      }
    };
    fetchTournament();
  }, [gameId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard!");
  };

  const handleShare = () => {
    setShareModal({
      ...INITIAL_MODAL_STATE,
      visible: true,
      onHide: hideShareModal,
    });
  };

  const hideShareModal = () => {
    setShareModal(INITIAL_MODAL_STATE);
  };

  const handleCodeSubmit = async () => {
    if (inputCode.length !== 6) {
      toast.error("Code must be exactly 6 characters");
      return;
    }
    setCodeLoading(true);
    try {
      const json = await patchRequest({
        url: `/celebrity/tournament/${gameId}/code`,
        detail: { joiningCode: inputCode },
      });

      if (json.success) {
        toast.success("Joining code set successfully!");
        setCode(inputCode);
        setShowCodeModal(false);
      } else {
        toast.error(json.message || "Failed to set code");
      }
    } catch (err) {
      toast.error(err.message || "Error setting joining code");
    } finally {
      setCodeLoading(false);
    }
  };

  if (loading) {
    return <div className="text-white text-center p-10">Loading...</div>;
  }

  return (
    <div className="bg-[#130828] min-h-svh p-3 font-poppins relative">
      {/* Tournament Code Modal */}
      {showCodeModal && (
        <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-4 text-black">
              Set Tournament Code
            </h2>
            <input
              type="text"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase())}
              maxLength={6}
              className="w-full border px-4 py-2 mb-4 rounded-md text-center tracking-widest text-lg"
              placeholder="Enter 6-letter code"
            />
            <button
              onClick={handleCodeSubmit}
              disabled={codeLoading}
              className={`px-4 py-2 rounded-lg w-full transition font-semibold ${
                codeLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#56920D] text-white hover:bg-[#85bd42]"
              }`}
            >
              {codeLoading ? "Setting..." : "Confirm Code"}
            </button>
          </div>
        </div>
      )}

      <div className="relative flex items-center justify-center py-10">
        <div className="absolute -left-1">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white"
          >
            <CustomSvg name="arrow-left" width={20} height={20} color="#fff" />
            <AiOutlineArrowLeft size={24} />
          </button>
        </div>
        <h2 className="text-xl font-bold bg-gradient-to-r from-[#C6BCFF] to-[#3F9217] bg-clip-text text-transparent text-center">
          Host Game
        </h2>
      </div>

      <div className="flex items-center text-white text-center justify-center gap-2 bg-gray-800 px-4 py-3 rounded-lg text-lg font-bold tracking-widest mb-3">
        <span>{code}</span>
        <button
          onClick={handleCopy}
          className="text-[#56920D] hover:text-[#85bd42]"
        >
          <FiCopy size={18} />
        </button>
      </div>

      <p className="text-center text-sm text-gray-300 mb-6 px-4">
        PIN & QR Code are unique and different for each competition, you can
        invite your friends to join the competition
      </p>

      <button
        onClick={handleShare}
        className="flex justify-center mx-auto items-center gap-2 bg-[#56920D] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#85bd42] transition"
      >
        <FiShare2 size={18} />
        Share
      </button>

      <ShareModal modalProps={shareModal} gameName={gameId} lobbyCode={code} />
    </div>
  );
};

export default TournamentCode;
